import { Money } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'
import { roundMoneyForDisplay } from '@shared/lib/money/display-reconciliation'
import type { SaleDetail } from './sale.contract'
import type { SaleJourney } from './sale-grouping'

export function saleDetailJourney(sale: SaleDetail): SaleJourney {
  return {
    sale,
    children: (sale.funnelStages ?? []).map((stage) => ({
      ...stage,
      id: stage.saleId,
      buyerName: sale.buyerName,
      createdAt: stage.createdAt ?? sale.createdAt,
    })),
  }
}

export interface JourneyParticipantResult {
  original: MonetaryValue
  losses: MonetaryValue
  fxLosses: MonetaryValue
  paidFees: MonetaryValue
  adjusted: MonetaryValue | null
}

/** The API already scopes event amounts to the viewer. Recovery is not a second loss. */
export function summarizeJourneyParticipantResult(sale: SaleDetail): JourneyParticipantResult {
  const wasPaid = (charge: { status: SaleDetail['status'] }) =>
    charge.status !== 'PENDING' && charge.status !== 'FAILED'
  const currency = sale.grossAmount.currency
  let original = new Money(0, currency)
  let losses = new Money(0, currency)
  let fxLosses = new Money(0, currency)
  let paidFees = new Money(0, currency)
  let complete = true

  for (const charge of [sale, ...(sale.funnelStages ?? [])]) {
    if (!wasPaid(charge)) continue
    if (charge.grossAmount.currency !== currency) complete = false
    // Match the original composition: each persisted receivable/reserve is displayed independently.
    for (const value of [charge.netAmount, charge.reserveAmount]) {
      if (value.currency !== currency) complete = false
      else original = original.add(Money.fromMonetaryValue(roundMoneyForDisplay(value)))
    }
    const events = charge.financialEvents ?? []
    if (events.length === 0 && (charge.status === 'REFUNDED' || charge.status === 'CHARGED_BACK'))
      complete = false

    for (const event of events) {
      const reversed = event.state === 'REVERSED' || event.kind === 'DISPUTE_WON'
      if (reversed) {
        const collected = event.recoveredAmount
        const returned = event.returnedAmount
        if (
          event.recoveryVerified !== true ||
          !returned ||
          collected.currency !== currency ||
          returned.currency !== currency
        ) {
          complete = false
        } else {
          const pendingReturn = Money.fromMonetaryValue(collected).subtract(
            Money.fromMonetaryValue(returned),
          )
          if (pendingReturn.isNegative()) complete = false
          else if (event.kind === 'FX_LOSS')
            fxLosses = fxLosses.add(
              Money.fromMonetaryValue(roundMoneyForDisplay(pendingReturn.toMonetaryValue())),
            )
          else
            losses = losses.add(
              Money.fromMonetaryValue(roundMoneyForDisplay(pendingReturn.toMonetaryValue())),
            )
        }
      } else {
        if (event.affectedAmount.currency !== currency) complete = false
        else if (event.kind === 'FX_LOSS')
          fxLosses = fxLosses.add(
            Money.fromMonetaryValue(roundMoneyForDisplay(event.affectedAmount)),
          )
        else
          losses = losses.add(Money.fromMonetaryValue(roundMoneyForDisplay(event.affectedAmount)))
      }

      if (event.feeAmount || event.feeRecoveredAmount) {
        // Canceling a liability does not prove that collected money was returned.
        if (reversed) {
          if (
            event.recoveryVerified !== true ||
            !event.feeRecoveredAmount ||
            !event.feeReturnedAmount ||
            event.feeRecoveredAmount.currency !== currency ||
            event.feeReturnedAmount.currency !== currency
          ) {
            complete = false
          } else {
            const pendingReturn = Money.fromMonetaryValue(event.feeRecoveredAmount).subtract(
              Money.fromMonetaryValue(event.feeReturnedAmount),
            )
            if (pendingReturn.isNegative()) complete = false
            else
              paidFees = paidFees.add(
                Money.fromMonetaryValue(roundMoneyForDisplay(pendingReturn.toMonetaryValue())),
              )
          }
          continue
        }
        const recovered =
          event.feeRecoveredAmount ??
          (event.feeAmount &&
          event.feeOutstandingAmount &&
          event.feeAmount.currency === event.feeOutstandingAmount.currency
            ? Money.fromMonetaryValue(event.feeAmount)
                .subtract(Money.fromMonetaryValue(event.feeOutstandingAmount))
                .toMonetaryValue()
            : null)
        if (
          !recovered ||
          recovered.currency !== currency ||
          Money.fromMonetaryValue(recovered).isNegative()
        ) {
          complete = false
        } else {
          paidFees = paidFees.add(Money.fromMonetaryValue(roundMoneyForDisplay(recovered)))
        }
      }
    }
  }

  return {
    original: original.toMonetaryValue(),
    losses: losses.toMonetaryValue(),
    fxLosses: fxLosses.toMonetaryValue(),
    paidFees: paidFees.toMonetaryValue(),
    adjusted: complete
      ? original.subtract(losses).subtract(fxLosses).subtract(paidFees).toMonetaryValue()
      : null,
  }
}
